<script lang="ts">
  import { makeApiRequest } from "$lib/utils/api";
  import { formatDistanceToNow } from "date-fns";

  import * as DropdownMenu from "$ui/dropdown-menu";

  import * as AlertDialog from "$ui/alert-dialog";
  import * as Drawer from "$ui/drawer";
  import * as Table from "$ui/table";
  import { Button } from "$ui/button";
  import { LoadingSpinner } from "$ui/loading-spinner";

  import { isArrayEqual } from "$utils/helpers";
  import { ORG_ROLES } from "$utils/constants";

  import DotsIcon from "$assets/icons/dots.svelte";
  import EditIcon from "$assets/icons/edit.svelte";
  import BinIcon from "$assets/icons/bin.svelte";
  import SectionTitle from "$lib/components/shared/SectionTitle.svelte";
  import { selectedOrgStore, type OrgMember } from "$lib/stores/orgs";
  import { authStateStore, userStore } from "$lib/stores/user";
  import OrgMembersForm from "$lib/components/identity/OrgMembersForm.svelte";
  import PageAccessDenied from "../PageAccessDenied.svelte";

  let isLoading: boolean = true;
  let filteredMembers: OrgMember[] = [];

  let drawerOpen = false;
  let confirmModalOpen = false;
  let isDeleting: boolean = false;
  let errorMessage: string = "";
  let selectedMember: OrgMember | undefined = undefined;

  $: org = $selectedOrgStore;
  $: auth = $authStateStore;
  $: userEmail = $authStateStore.userInfo?.email?.toLowerCase();
  $: canEdit =
    !!userEmail &&
    !!org?.members &&
    !!org?.members.find((o) => o.email?.toLowerCase() === userEmail && o.claims.includes("org:write"));
  $: canView =
    canEdit ||
    (!!userEmail &&
      !!org?.members &&
      !!org?.members.find((o) => o.email?.toLowerCase() === userEmail && o.claims.includes("org:read")));

  $: {
    filteredMembers = org?.members ?? [];
    filteredMembers.sort((a, b) => a.modified && a.modified.valueOf() - b.modified.valueOf());
    isLoading = false;
  }

  const onAddMember = () => {
    selectedMember = undefined;
    drawerOpen = true;
  };

  const onEditMember = (email: string) => {
    selectedMember = org?.members.find((member) => member.email?.toLowerCase() === email.toLowerCase());
    drawerOpen = true;
  };

  const onDeleteConfirm = (email: string) => {
    selectedMember = org?.members.find((member) => member.email?.toLowerCase() === email.toLowerCase());
    confirmModalOpen = true;
  };

  const onDeleteMember = async () => {
    if (!selectedMember) {
      return;
    }
    try {
      isDeleting = true;
      await makeApiRequest(`${org?.resourcePrefix}/members/${selectedMember.email}`, {}, "DELETE");
      confirmModalOpen = false;
    } catch (error: any) {
      errorMessage = "Error deleting Member. " + error?.message;
      console.error("Error deleting Member:", error);
    } finally {
      isDeleting = false;
    }
  };
</script>

{#if canView}
  <div class="grow px-6 lg:px-10 py-5 sm:py-8 lg:py-10 text-offblack">
    <SectionTitle>Members</SectionTitle>
    <div class="flex flex-col-reverse xs:flex-row justify-between xs:items-center gap-4">
      <div class="flex justify-between w-full px-6 py-3 gap-4 divide-x-2 items-center">
        <div></div>
        <Drawer.Root bind:open={drawerOpen}>
          <Drawer.Trigger disabled={!canEdit}>
            <Button on:click={onAddMember} class="w-full min-h-[46px]" disabled={!canEdit}>+ Add Members</Button>
          </Drawer.Trigger>
          <Drawer.Content>
            <Drawer.Header class="pt-6 px-6 pb-0 relative">
              <Drawer.Title class="text-xl">
                {selectedMember ? "Edit Member" : "Add New Members"}
              </Drawer.Title>
              <Drawer.Description class="p text-base">
                {selectedMember ? "Make changes to an Existing Member" : "Invite new members to join your team"}
              </Drawer.Description>
            </Drawer.Header>
            <div class="p-6 flex-1 flex flex-col">
              <OrgMembersForm
                initialValues={selectedMember ? { members: [selectedMember] } : undefined}
                isEdit={selectedMember ? true : false}
                onSuccess={() => {
                  drawerOpen = false;
                }}
              />
            </div>
          </Drawer.Content>
        </Drawer.Root>
      </div>
    </div>
    {#if isLoading}
      <div class="flex justify-center items-center">
        <LoadingSpinner />
      </div>
    {:else}
      <div class="card">
        <Table.Root>
          <Table.Header>
            <Table.Row class="hidden sm:table-row">
              <Table.Head class="font-semibold">User</Table.Head>
              <Table.Head class="font-semibold hidden xs:table-cell">Role</Table.Head>
              <Table.Head class="font-semibold hidden xs:table-cell">Last Modified</Table.Head>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {#if filteredMembers.length === 0}
              <Table.Row>
                <Table.Cell class="text-center py-8" colspan={4}>No members found</Table.Cell>
              </Table.Row>
            {/if}
            {#each filteredMembers as member (member.email)}
              <Table.Row class="py-2 sm:py-0">
                <Table.Cell data-label="User" class="font-medium truncate">
                  {member.email}
                </Table.Cell>
                <Table.Cell data-label="Role">
                  {ORG_ROLES.find((role) => isArrayEqual(role.value, member.claims || []))?.label ?? "-"}
                </Table.Cell>
                <Table.Cell data-label="Modified"
                  >{member.modified && formatDistanceToNow(member.modified, { addSuffix: true })}</Table.Cell
                >
                <Table.Cell
                  class="flex sm:justify-end !absolute sm:!relative top-1 -right-7 sm:top-auto sm:right-auto before:!hidden !w-auto"
                >
                  <DropdownMenu.Root>
                    <DropdownMenu.Trigger asChild let:builder>
                      <Button variant="ghost" builders={[builder]} class="[&>svg]:rotate-90 max-h-[32px] px-3">
                        <DotsIcon />
                      </Button>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Content class="w-40" side="right" align="start" sideOffset={10}>
                      <DropdownMenu.Group>
                        <DropdownMenu.Item
                          on:click={() => onEditMember(member.email)}
                          Icon={EditIcon}
                          disabled={!canEdit}
                        >
                          Edit
                        </DropdownMenu.Item>
                        <DropdownMenu.Item
                          class="!text-danger-400"
                          on:click={() => onDeleteConfirm(member.email)}
                          Icon={BinIcon}
                          disabled={!canEdit}
                        >
                          Delete
                        </DropdownMenu.Item>
                      </DropdownMenu.Group>
                    </DropdownMenu.Content>
                  </DropdownMenu.Root>
                </Table.Cell>
              </Table.Row>
            {/each}
          </Table.Body>
        </Table.Root>
      </div>
    {/if}
  </div>
  <AlertDialog.Root
    open={confirmModalOpen}
    closeOnOutsideClick={true}
    onOpenChange={(isOpen) => {
      if (!isOpen) {
        confirmModalOpen = false;
        selectedMember = undefined;
      }
    }}
  >
    <AlertDialog.Content>
      <AlertDialog.Header>
        <AlertDialog.Title>Are You Sure?</AlertDialog.Title>
        <AlertDialog.Description>
          You are about to delete member:
          <span class="font-medium">
            {selectedMember?.email}
          </span>
        </AlertDialog.Description>
      </AlertDialog.Header>
      {#if errorMessage}
        <div class="text-danger-400 text-sm">{errorMessage}</div>
      {/if}
      <AlertDialog.Footer>
        <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
        <Button variant="destructive" on:click={onDeleteMember} disabled={isDeleting}>
          {isDeleting ? "Deleting..." : "Delete Member"}
        </Button>
      </AlertDialog.Footer>
    </AlertDialog.Content>
  </AlertDialog.Root>
{:else}
  <PageAccessDenied />
{/if}
